import type { UserDto } from '@act/shared/data-transfer-objects';
import {
  Action,
  ActionMenu,
  FlexContainer,
  IconLibrary,
  MemberStatusTag,
} from '@act/storybook';
import { useMobile } from '@act/utilities/mobile-context';
import { Box, IconButton, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { getMemberPageUrl } from 'react-components/app/routing/routes';
import useMemberStatusChangeRequestDialog from 'react-components/dialog-forms/hooks/use-member-status-change-request-dialog';
import useTransferRequestDialog from 'react-components/dialog-forms/hooks/use-transfer-request-dialog';
import { useNavigate } from 'react-router-dom';
import { AvonUrlHelper } from 'utilities/avonUrls';
import {
  displayName,
  pronounsFormatter,
} from 'utilities/formatters/display-name';
import { useAppLocation } from '../app/routing/useAppLocation';

interface MemberHeaderProps {
  user: UserDto;
  selectedSection: string;
  updateSelectedSection: (update: string) => void;
}

export const MemberHeader = ({ user }: MemberHeaderProps) => {
  const navigate = useNavigate();
  const appLocation = useAppLocation();
  const { isMobile } = useMobile();
  const actionItems: Action[] = [];

  const isOnlyContentPage = appLocation === 'avon-patient';

  if (!user) {
    return null;
  }

  if (user.avonId) {
    actionItems.push({
      display: 'View Member in Avon',
      callback: () => {
        window.open(AvonUrlHelper.getMemberProfileUrl(user.avonId), '_blank');
      },
    });
  }

  const openTransferRequestDialog = useTransferRequestDialog();
  const openMemberStatusChangeRequestDialog =
    useMemberStatusChangeRequestDialog();

  return (
    <FlexContainer
      direction="row"
      padding={isMobile ? '8px 0' : '12px 24px'}
      height="100%"
      width="100%"
      alignItems="center"
      gap={isMobile ? 0 : 2}
    >
      {!isOnlyContentPage && (
        <Box sx={{ flexShrink: 0 }}>
          <IconButton
            color="primary"
            aria-label="back"
            onClick={() => navigate(-1)}
          >
            <IconLibrary iconName="ArrowLeft" size={32} weight="regular" />
          </IconButton>
        </Box>
      )}
      <Box
        sx={{
          flex: '0 1 auto',
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <Typography
          variant="headlineMedium"
          color="primary"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {displayName(user, { onlyLastInitial: isMobile })}
        </Typography>
      </Box>
      {user.pronouns && !isMobile && (
        <Box sx={{ marginLeft: 0, flexShrink: 0 }}>
          <Typography
            variant="bodyLarge"
            color="primary"
            sx={{
              overflow: 'hidden',
            }}
          >
            ({pronounsFormatter(user.pronouns)})
          </Typography>
        </Box>
      )}
      <Box sx={{ marginLeft: isMobile ? 2 : 0, flexShrink: 0 }}>
        <MemberStatusTag status={user.memberStatus} />
      </Box>
      <Box
        sx={{
          marginLeft: 'auto',
          marginRight: isMobile ? 0 : '16px',
          flexShrink: 0,
        }}
      >
        <ActionMenu
          actions={[
            {
              display: 'Request member transfer',
              callback: () =>
                openTransferRequestDialog({ requestMember: user }),
            },
            {
              display: 'Request status change',
              callback: () =>
                openMemberStatusChangeRequestDialog({
                  memberId: user.id,
                  effectiveAt: DateTime.now().toMillis(),
                }),
            },
            {
              display: 'Copy member link',
              callback: () => {
                const memberPageUrl = getMemberPageUrl(
                  window.location.pathname,
                  user.assignedGuideId,
                  user.id
                );
                void navigator.clipboard.writeText(memberPageUrl);
              },
            },
            ...actionItems,
          ]}
          horizontalAlign="right"
          color="primary"
        />
      </Box>
    </FlexContainer>
  );
};
